


import {
  defineComponent,
  computed,
  onMounted,
  reactive,
  ref,
} from '@vue/composition-api';

import { SearchState } from '@/models/externalReports/standard/standardMap';
import { useStore } from '@/hooks/useStore';
import { useRoute } from '@/hooks/useRoute';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import RealtimeView from './components/RealtimeView/index.vue';
import ReportsView from './components/ReportsView/index.vue';
import ManualDownloadLinks from './components/common/ManualDownloadLinks/index.vue';
import MapMiscInfo from './components/common/MapMiscInfo.vue';
import { dtFormat } from '@/lib/dateHelper';
import { initSearchConds, validSearchDateRange } from '@/components/ExternalReports/Standard/utils';
import {
  VIEW_MODE_REALTIME,
  VIEW_MODE_REPORT,
} from '@/components/ExternalReports/Standard/consts';
import { ITEMS_PER_PAGE_DEFAULT, CURRENT_PAGE_DEFAULT } from '@/consts/externalReports/standard/report_print';
import { EXTERNAL_REPORT_TYPE } from '@/consts/externalReports/standard';
import useStandardCars, { provideStandardCars } from '@/components/ExternalReports/Standard/composables/useCars';
import { provideStandardReports, useStandardReports } from '@/components/ExternalReports/Standard/composables/useReports';
import { useRequestGuard } from '@/composables/useRequestGuard';
import type {
  ExternalReportsKeyName,
  ExternalReportChoices,
  ExternalReportsLabelPc,
  ExternalReportSettings,
} from '@/models/apis/externalReports/external_report_settings';
import type { ExternalReportAbility } from '@/models/externalReports/externalReportAbility';
import { EXTERNAL_REPORT_ABILITY_DRIVER } from '@/consts/externalReports/external_reports';

interface SelectOption {
  id: number;
  name: string;
}

export default defineComponent({
  setup() {
    const viewMode = ref<string>(VIEW_MODE_REALTIME);

    const searchState = reactive<SearchState>({
      searchConds: {
        hanName: null,
        status: '全て',
        dateFrom: new Date(),
        dateTo: new Date(),
      },
      decidedSearchConds: {
        hanName: null,
        status: '全て',
        dateFrom: new Date(),
        dateTo: new Date(),
      },
    });
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      console.error(errorMessage);
    });

    const store = useStore();
    const userState = store.state.user;
    const isRealtimeMode = computed<boolean>(() => {
      return viewMode.value === VIEW_MODE_REALTIME;
    });
    const isReportMode = computed<boolean>(() => {
      return viewMode.value === VIEW_MODE_REPORT;
    });
    const standardUserSettings = ref<ExternalReportSettings | null>(null);
    const externalReportAbility = computed<ExternalReportAbility | null>(() => {
      return userState?.external_report_abilities?.find(
        ability => ability.external_report_type === EXTERNAL_REPORT_TYPE,
      ) ?? null;
    });
    const standardChoices = computed<ExternalReportChoices>(() => {
      if (!standardUserSettings.value) return {} as ExternalReportChoices;

      return standardUserSettings.value.choices;
    });
    const workHans = computed<SelectOption[]>(() => {
      if (!standardChoices.value?.work_hans) return [];

      return standardChoices.value.work_hans.map((e, index) => ({ id: index, name: e }));
    });
    const isDateParamOk = computed<boolean>(() => {
      return validSearchDateRange(searchState.searchConds);
    });
    const statusChoices = computed<ExternalReportsKeyName[]>(() => {
      if (!standardChoices.value?.status) return [];
      const standardStatus = standardChoices.value?.status || {};
      return [
        { key: '全て', name: '全て' },
        { key: standardStatus.running.key, name: standardStatus.running.name },
        { key: standardStatus.working.key, name: standardStatus.working.name },
        { key: standardStatus.stopped.key, name: standardStatus.stopped.name },
      ];
    });
    const pcLabels = computed<ExternalReportsLabelPc>(() => {
      return standardUserSettings.value?.pc || {
        title: {},
        button: {},
        field_label: {},
        table_header: {},
      } as ExternalReportsLabelPc;
    });

    const { router } = useRoute();

    onMounted(async() => {
      await doRequest(async() => {
        await waitForUserAndMasters();
      }, '初期化に失敗しました。');
      standardUserSettings.value = window.master.externalReportSettings?.find(
        setting => setting.external_report_type === EXTERNAL_REPORT_TYPE,
      ) ?? null;
      if (!externalReportAbility.value || !standardUserSettings.value) {
        router.replace({ name: 'Login' });
        return;
      }
      searchState.searchConds = initSearchConds();
      searchState.decidedSearchConds = { ...searchState.searchConds };
      const abilities = standardUserSettings.value.choices.abilities;
      const ability = abilities?.find(ability => ability.key === externalReportAbility.value?.ability_id);
      if (!ability) {
        router.replace({ name: 'Login' });
        return;
      }

      if (ability.name.endsWith(EXTERNAL_REPORT_ABILITY_DRIVER)) {
        router.replace({ name: 'Login' });
      }
    });

    const {
      filterCars,
      clearCars,
    } = (() => {
      const injectedState = provideStandardCars(searchState);
      return useStandardCars(injectedState);
    })();

    const {
      searchReports,
      clearReports,
    } = (() => {
      const reportState = provideStandardReports(searchState);
      return useStandardReports(reportState);
    })();

    const changeViewMode = async(mode: string) => {
      if (mode === viewMode.value) return;

      searchState.searchConds = initSearchConds();
      searchState.decidedSearchConds = { ...searchState.searchConds };
      viewMode.value = mode;
      if (mode === VIEW_MODE_REALTIME) {
        clearReports();
      }
      if (mode === VIEW_MODE_REPORT) {
        clearCars();
      }
    };

    const onClickReportPrintAllButton = () => {
      const routeObj = {
        name: 'PrintExternalReportsStandardReportAll',
        params: {
          dateFrom: dtFormat(searchState.searchConds.dateFrom, 'yyyy-mm-dd'),
          dateTo: dtFormat(searchState.searchConds.dateTo, 'yyyy-mm-dd'),
          hanName: searchState.searchConds.hanName || 'all',
          currentPage: CURRENT_PAGE_DEFAULT.toString(),
          itemsPerPage: ITEMS_PER_PAGE_DEFAULT.toString(),
        },
      };
      const obj = router.resolve(routeObj);
      window.open(obj.href, '_blank');
    };

    return {
      searchState,
      userState,
      isRequesting,
      // const
      statusChoices,
      standardUserSettings,
      VIEW_MODE_REALTIME,
      VIEW_MODE_REPORT,
      // computed
      isRealtimeMode,
      isReportMode,
      workHans,
      isDateParamOk,
      pcLabels,
      // methods
      changeViewMode,
      filterCars,
      searchReports,
      onClickReportPrintAllButton,
    };
  },
  components: {
    RealtimeView,
    ReportsView,
    ManualDownloadLinks,
    MapMiscInfo,
  },
});
